"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import * as Flags from "country-flag-icons/react/3x2";

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export function countryName(code: string): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/** All ISO alpha-2 codes the flag set knows, sorted by display name. */
const ALL_CODES: string[] = Object.keys(Flags)
  .filter((k) => /^[A-Z]{2}$/.test(k))
  .sort((a, b) => countryName(a).localeCompare(countryName(b)));

export function CountryFlag({ code, size = 20 }: { code: string; size?: number }) {
  const Flag = (Flags as Record<string, React.ComponentType<{ style?: React.CSSProperties }> | undefined>)[code];
  if (!Flag) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <Flag style={{ height: size, width: "auto", maxWidth: "none" }} />
    </span>
  );
}

export function CountrySelect({
  value,
  onChange,
  placeholder = "Select country…",
}: {
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? ALL_CODES.filter((c) => countryName(c).toLowerCase().includes(q) || c.toLowerCase().startsWith(q))
      : ALL_CODES;
    return list.slice(0, 50);
  }, [query]);

  return (
    <div className="relative" ref={ref}>
      <button type="button" className="field flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
        {value ? (
          <>
            <CountryFlag code={value} />
            <span className="min-w-0 flex-1 truncate">{countryName(value)}</span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate" style={{ color: "var(--c-ink-soft)" }}>
            {placeholder}
          </span>
        )}
        <ChevronsUpDown size={14} style={{ color: "var(--c-ink-soft)" }} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-[6px] border"
          style={{ background: "var(--c-white)", borderColor: "var(--c-hairline)" }}
        >
          <input
            ref={inputRef}
            className="w-full border-b px-3 py-2 text-[14px] font-light outline-none"
            style={{ borderColor: "var(--c-hairline)" }}
            placeholder="Search countries…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="max-h-[220px] overflow-y-auto py-1">
            {matches.map((c) => (
              <button
                key={c}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[14px] transition-colors duration-150 hover:bg-[var(--c-cream)]"
                style={{ fontWeight: c === value ? 500 : 300 }}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <CountryFlag code={c} />
                <span className="min-w-0 flex-1 truncate">{countryName(c)}</span>
                <span className="mono text-[11px]" style={{ color: "var(--c-ink-soft)" }}>
                  {c}
                </span>
                {c === value && <Check size={14} />}
              </button>
            ))}
            {matches.length === 0 && (
              <p className="px-3 py-2 text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                No matches
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
